import { DefaultDeviceOptions } from "../constants/DevicesConstants";
import { PromptTypes } from "../constants/PromoTypesConstants";
import { REDIRECTS, REDIRECTS_ACTION_OBJ } from "../constants/PromptActionsConstants";
import { getDisplayMetricsText } from "../metricsHelpers";
import { isLocalized } from "./promptHelpers";
import {
  isAccept2Enabled,
  isDeclineEnabled,
  isDismissEnabled,
  isTimeoutEnabled,
} from "@/utils/prompts/promptHelpers";
export const isRedirectAction = actionName => REDIRECTS.includes(actionName);

/**
 * Builds the settings key holding the redirect delay for a given interaction type.
 * @param {string} interactionType one of accept, accept2, decline, dismiss, timeout.
 * @returns {string} settings key, e.g. "rf_settings_accept_redirect_delay_seconds".
 */
export const redirectDelayActionName = interactionType =>
  `rf_settings_${interactionType}_redirect_delay_seconds`;

export const readOnlyActions = (prompt, currLanguage) => {
  return isLocalized(prompt)
    ? { ...prompt.actions, ...(prompt.parsed_translations[currLanguage] || {}) }
    : prompt.actions;
};

export const fetchValues = (input, currAppConnectors, prompt, currLanguage) => {
  const values = input.possible_values;
  if (input.input_type !== "select") return null;

  if (input.values_included) {
    if (input.dynamic_value_name) {
      const dynamicValues = [];
      values.forEach(item => {
        const value = { id: item.id };
        const [source, key] = item.name.split(".");
        if (source === "actions") {
          const action = readOnlyActions(prompt, currLanguage)[key];
          if (action) {
            value.name = action;
            dynamicValues.push(value);
          }
        }
      });
      return dynamicValues;
    }
    return values;
  }
  const [provider, property] = values.split(".");
  const providerValues = currAppConnectors[provider] || {};
  return providerValues[property];
};

export const displayConnectorAction = ({
  key,
  value,
  inputs,
  currAppConnectors,
  prompt,
  currLanguage,
}) => {
  let displayKey = key;
  let displayValue = value;
  const displayData = inputs.find(input => input.value_description === key);
  if (displayData) {
    displayKey = displayData.value_label;
    if (displayData.hide_id) {
      const possibleValues = fetchValues(displayData, currAppConnectors, prompt, currLanguage);
      if (possibleValues) {
        const name = possibleValues.find(possibleValue => possibleValue.id === value);
        if (name) {
          displayValue = name.name;
        }
      }
    }
  }
  return `${displayKey}: ${displayValue}`;
};

export const interactionTypesExtended = (prompt, app) => {
  const shouldShowOnlyPrimary =
    prompt.path_type === PromptTypes.text.value ||
    ([PromptTypes.horizontal.value, PromptTypes.vertical.value, PromptTypes.tile.value].includes(
      prompt.path_type,
    ) &&
      ["container", "none"].includes(prompt.actions?.rf_settings_tile_interaction));

  const button1Text = getDisplayMetricsText(prompt, "rf_retention_button1_text");
  const button2Text = getDisplayMetricsText(prompt, "rf_retention_button2_text");
  const button3Text = getDisplayMetricsText(prompt, "rf_retention_button3_text");

  const isAppleNewDevice =
    !app?.flags?.legacy_apple_editor &&
    [DefaultDeviceOptions.ios.value, DefaultDeviceOptions.tv_os.value].includes(prompt.device_type);
  const isAndroidNewDevice =
    app?.flags?.new_android_editor &&
    [DefaultDeviceOptions.android_os.value, DefaultDeviceOptions.android_tv.value].includes(
      prompt.device_type,
    );

  const isNewDevice = isAppleNewDevice || isAndroidNewDevice;

  const isButton1Disabled = isNewDevice && !button1Text && !shouldShowOnlyPrimary;
  const isButton2Disabled = isNewDevice && !button2Text;
  const isButton3Disabled = isNewDevice && !button3Text;

  const types = [];

  if (prompt.path_type !== PromptTypes.invisible.value) {
    types.push({ value: "impression", text: "Seen" });
  }

  if (!isButton1Disabled)
    types.push({
      value: "accept",
      text: shouldShowOnlyPrimary
        ? "Primary"
        : button1Text || (prompt.path_type === PromptTypes.invisible.value ? "Accept" : "Button 1"),
    });

  if (prompt.path_type === PromptTypes.invisible.value)
    return types.concat([{ value: "holdout", text: "Holdout" }]);
  if (!prompt.actions) return types;

  if (isDismissEnabled(prompt)) {
    types.push({ value: "dismiss", text: "Dismiss" });
  }

  if (isTimeoutEnabled(prompt)) {
    types.push({ value: "timeout", text: "Timeout" });
  }

  if (shouldShowOnlyPrimary) return types;

  if (isAccept2Enabled(prompt) && !isButton2Disabled) {
    types.push({
      value: "accept2",
      text: button2Text || "Button 2",
    });
  }

  if (isDeclineEnabled(prompt) && !isButton3Disabled) {
    types.push({
      value: "decline",
      text: button3Text || "Button 3",
    });
  }

  return types;
};

export const interactionTypes = prompt => {
  const types = ["accept"];
  if (prompt.path_type === "invisible") return types;
  types.push("impression");
  if (isDismissEnabled(prompt)) {
    types.push("dismiss");
  }

  if (isTimeoutEnabled(prompt)) {
    types.push("timeout");
  }

  if (isAccept2Enabled(prompt)) {
    types.push("accept2");
  }

  if (isDeclineEnabled(prompt)) {
    types.push("decline");
  }

  return types;
};

export const promoInputsEnabled = prompt => {
  return !!(
    prompt.actions.rf_retention_promo_input_1_type ||
    prompt.actions.rf_retention_promo_input_2_type ||
    prompt.actions.rf_retention_promo_input_3_type
  );
};

/**
 * Whether a connector-action's `"provider.key"` app-dependency is satisfied.
 *
 * @param {string} provider - Provider key, e.g. "recurly".
 * @param {string} key - Dependency field on that provider, e.g. "api_key".
 * @param {object} providers - `app.providers`.
 * @returns {boolean} Whether the dependency is met.
 * @example
 * isProviderDependencyMet("stripe", "api_key", { stripe: { api_key: "sk_x" } }); // true
 */
export const isProviderDependencyMet = (provider, key, providers) => !!providers?.[provider]?.[key];

/**
 * Whether a connector action should be disabled for a given interaction.
 *
 * @param {object} args - Connector action's `args` (dependencies, app_dependencies).
 * @param {string} interaction - Interaction being checked, e.g. "accept".
 * @param {object} prompt - Prompt the action belongs to.
 * @param {object} providers - `app.providers`.
 * @param {(string[]|{value: string}[])} [enabledInteractionTypes] - Interaction types enabled for
 *   this prompt. Defaults to `interactionTypes(prompt)`; pass `interactionTypesExtended(prompt, app)`
 *   where that richer, app/device-aware list is what's actually rendered (e.g. the connector-action
 *   picker), so this stays in sync with it instead of re-deriving a plain list on its own.
 * @returns {boolean} Whether the action is disabled.
 */
export const isActionDisabled = (
  args,
  interaction,
  prompt,
  providers,
  enabledInteractionTypes = interactionTypes(prompt),
) => {
  const dependencies = args.dependencies || {};
  const appDependencies = args.app_dependencies;
  const providerDependencies = appDependencies && appDependencies.providers;

  let dependenciesMet = true;
  let appDependenciesMet = true;
  const interactionEnabled = enabledInteractionTypes.find(
    type => type === interaction || type?.value === interaction,
  );
  if (dependencies.promo_inputs?.length === 1 && dependencies.promo_inputs[0] === "all") {
    dependenciesMet =
      promoInputsEnabled(prompt) && (interaction === "accept" || interaction === "accept2");
  }

  if (providerDependencies && providerDependencies.length) {
    for (let i = 0; i < providerDependencies.length; i++) {
      const dep = providerDependencies[i];
      const [provider, key] = dep.split(".");

      if (!isProviderDependencyMet(provider, key, providers)) {
        appDependenciesMet = false;
        break;
      }
    }
  }
  return !(dependenciesMet && appDependenciesMet && interactionEnabled);
};

export const getActiveActions = (
  prompt,
  actionGroupClientActions,
  extendedByAppContext = false,
) => {
  const validInteractionTypes = {
    accept_redirect: true,
    accept2_redirect: true,
    decline_redirect: true,
    dismiss_redirect: true,
    timeout_redirect: true,
    ...(extendedByAppContext
      ? interactionTypesExtended(prompt, extendedByAppContext)
      : interactionTypes(prompt)
    ).reduce((hash, item) => {
      hash[item] = true;
      return hash;
    }, {}),
  };
  const redirects = Object.entries(REDIRECTS_ACTION_OBJ).filter(
    ([key, item]) => !!prompt.actions?.[item]?.length && validInteractionTypes[key],
  );

  const websiteActions = (actionGroupClientActions || []).map(item => [
    item.interaction_type,
    item.interaction_type,
    item.args,
  ]);

  return redirects.concat(websiteActions);
};
